/**
 * CSV Export utility for dashboard tables.
 *
 * Extracts data from a TableData structure, handling colspan/rowspan
 * in headers, and produces a CSV string suitable for download.
 */

import type { TableData, TableHeader } from "./reportGenerator";

/**
 * A row description provided by the trends UI for the CSV export. Each
 * entry has a label (already including the scale suffix or P1/P2+/Total
 * tag the UI is showing) and the per-period cell strings already formatted
 * the same way they appear on screen.
 */
export interface TrendsCsvRow {
  category: string; // e.g., "Search", "Detail", "Homepage", "Search & Detail & Homepage"
  metric: string; // e.g., "Revenue USD (MM)" or "Coverage"
  segment: "Total" | "P1" | "P2+";
  /** One per period column, formatted strings. */
  values: string[];
}

/**
 * Build a CSV string from the Historical Trends tab's currently rendered
 * data. Columns are: Category, Metric, Segment, then one column per period
 * (using the period's display label like "W/E 03/21").
 */
export function exportHistoricalTrendsToCsv(
  viewLabel: string,
  product: "SP" | "SB",
  geoLabel: string,
  deviceLabel: string,
  currency: "USD" | "LOC",
  alignmentLabel: string,
  comparisonModeLabel: string,
  periodLabels: string[],
  rows: TrendsCsvRow[]
): string {
  const lines: string[] = [];

  // Title block — top of CSV gives the user the context for the view.
  const titleLine = `${product} ${geoLabel} ${deviceLabel} — Historical Trends — ${viewLabel}`;
  lines.push(escapeCell(titleLine));
  const meta = [
    `Currency: ${currency}`,
    `Alignment: ${alignmentLabel}`,
    `Compare: ${comparisonModeLabel}`,
  ];
  lines.push(meta.map(escapeCell).join(","));
  lines.push(""); // blank line separator

  // Header row.
  const header = ["Category", "Metric", "Segment", ...periodLabels];
  lines.push(header.map(escapeCell).join(","));

  // Data rows.
  for (const r of rows) {
    const cells = [r.category, r.metric, r.segment, ...r.values];
    lines.push(cells.map(escapeCell).join(","));
  }

  return lines.join("\n");
}

/** Escape a CSV cell value (wrap in quotes if it contains commas, quotes, or newlines). */
function escapeCell(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Expand multi-row headers with colspan/rowspan into a flat 2D grid.
 *
 * Each cell in the grid contains the header label. Cells spanned by
 * colspan or rowspan are filled with the spanning cell's label.
 */
function expandHeaders(headers: TableHeader[][]): string[][] {
  if (headers.length === 0) return [];

  // Determine total column count from the first row
  const totalCols = headers[0].reduce((sum, h) => sum + (h.colspan ?? 1), 0);
  const totalRows = headers.length;

  // Create empty grid
  const grid: (string | null)[][] = Array.from({ length: totalRows }, () =>
    Array(totalCols).fill(null)
  );

  // Fill grid respecting colspan and rowspan
  for (let ri = 0; ri < totalRows; ri++) {
    let ci = 0;
    for (const header of headers[ri]) {
      // Find next empty cell in this row
      while (ci < totalCols && grid[ri][ci] !== null) {
        ci++;
      }
      if (ci >= totalCols) break;

      const colspan = header.colspan ?? 1;
      const rowspan = header.rowspan ?? 1;

      for (let dr = 0; dr < rowspan && ri + dr < totalRows; dr++) {
        for (let dc = 0; dc < colspan && ci + dc < totalCols; dc++) {
          grid[ri + dr][ci + dc] = header.label;
        }
      }
      ci += colspan;
    }
  }

  // Replace any remaining nulls with empty string
  return grid.map((row) => row.map((cell) => cell ?? ""));
}

/**
 * Export a TableData structure to a CSV string.
 * If comparisonTable is provided, interleaves comparison rows after each primary row.
 */
export function exportTableDataToCsv(
  tableData: TableData,
  comparisonTable?: TableData
): string {
  const lines: string[] = [];

  // Expand headers into flat rows
  const headerGrid = expandHeaders(tableData.headers);
  for (const headerRow of headerGrid) {
    lines.push(headerRow.map(escapeCell).join(","));
  }

  // Data rows
  for (let i = 0; i < tableData.rows.length; i++) {
    const row = tableData.rows[i];
    const cells = [row.label, ...row.cells.map((c) => c.value)];
    lines.push(cells.map(escapeCell).join(","));

    if (comparisonTable) {
      const cmpRow = comparisonTable.rows[i];
      if (cmpRow) {
        const cmpCells = [
          `${cmpRow.label} (Comparison)`,
          ...cmpRow.cells.map((c) => c.value),
        ];
        lines.push(cmpCells.map(escapeCell).join(","));
      }
    }
  }

  return lines.join("\n");
}
